/**
 * "Top marketing assets" on Home: which of the brand's marketing assets brought players in the selected range.
 *
 * One grouped read of the Owners Hub Stats `daily_affiliate_activity` data set, aggregated on `campaign`. Every
 * tracking link carries a unique `campaign` token that signup stores verbatim (see `creatives/tracking-links-api.ts`),
 * so a group whose campaign is a link token belongs to that ONE link, hence one asset version. An asset owns one link
 * per version, so its figures are the sum over its links. A landing page's own link makes the page an asset of its
 * own (kind `landing_page`); assets routed through the page keep their own rows.
 *
 * Pure and React-free: the hook (`useTopMarketingAssets`) passes the payload and the brand's links.
 */
import type { TrackingLink } from '../creatives/tracking-links-api';
import { linkAssetName, linkAssetPath, linkLandingPage } from '../creatives/link-attribution';
import { campaignGroups, sumMetricField, VIDEO_STAT_DEFS, videoStatValue, type VideoStatDef } from '../creatives/video-stats';
import { groupDimension, groupMetrics, type Metrics } from './stats-shape';

/** How many assets the list shows. */
export const TOP_ASSETS_LIMIT = 5;

/** The data set and grouping the list reads (the same one the link stats in the composers read). */
export const TOP_ASSETS_STATS = {
  dataSet: 'daily_affiliate_activity',
  aggregateOn: ['campaign'],
} as const;

/** Deposit volume (money, scaled integer): the same KPI definition the link stats use. */
const DEPOSIT_DEF: VideoStatDef = VIDEO_STAT_DEFS.find((d) => d.id === 'deposit_amount') ?? {
  id: 'deposit_amount',
  fields: ['deposit_amount'],
  kind: 'money',
};

/** A landing page an asset's links go through. */
export interface TopAssetLanding {
  id: string;
  name: string;
  path: string;
}

/** One ranked asset. */
export interface TopAssetRow {
  /** Stable row key (kind + asset id). */
  key: string;
  /** The asset's best link (most FTDs, then registrations): its kind, asset and landing page drive the row. */
  link: TrackingLink;
  name: string;
  /** The asset's thumbnail ('' when it has none). */
  imageUrl: string;
  /** The asset's composer. */
  path: string;
  archived: boolean;
  /** The landing page the best link goes through (undefined: it goes straight to the casino). */
  landing?: TopAssetLanding;
  /** The asset IS a landing page (its own link): its row has no "via" (the page is the destination itself). */
  isLandingPage: boolean;
  /** The other destinations the asset's attributed links go through (other pages, or the casino). */
  otherDestinations: string[];
  registrations: number;
  ftds: number;
  /** How many deposits the asset's players made. */
  deposits: number;
  /** Deposit volume in the major currency unit. */
  depositAmount: number;
}

interface LinkFigures {
  link: TrackingLink;
  metrics: Metrics[];
  registrations: number;
  ftds: number;
  deposits: number;
  depositAmount: number;
}

/** FTDs desc, then registrations desc, then deposit volume desc. */
const byPerformance = (
  a: Pick<LinkFigures, 'ftds' | 'registrations' | 'depositAmount'>,
  b: Pick<LinkFigures, 'ftds' | 'registrations' | 'depositAmount'>,
): number => b.ftds - a.ftds || b.registrations - a.registrations || b.depositAmount - a.depositAmount;

const figuresOf = (link: TrackingLink, metrics: Metrics[]): LinkFigures => ({
  link,
  metrics,
  registrations: sumMetricField(metrics, 'registrations'),
  ftds: sumMetricField(metrics, 'ftds'),
  deposits: sumMetricField(metrics, 'deposits'),
  depositAmount: videoStatValue(metrics, DEPOSIT_DEF),
});

/** Identifies a link's destination: its landing page id, or '' for the casino. */
const destinationKey = (link: TrackingLink): string => linkLandingPage(link)?.id ?? '';

/**
 * Rank the brand's assets by what their links brought in the range: FTDs, then registrations. Assets with neither are
 * left out; at most `limit` rows. `casinoLabel` names the casino among an asset's other destinations. Pure.
 */
export const topMarketingAssets = (
  raw: unknown,
  byCampaign: ReadonlyMap<string, TrackingLink>,
  casinoLabel: string,
  limit: number = TOP_ASSETS_LIMIT,
): TopAssetRow[] => {
  // Each link's groups (a campaign normally answers once; several groups for one token simply add up).
  const perLink = new Map<string, { link: TrackingLink; metrics: Metrics[] }>();
  campaignGroups(raw).forEach((group) => {
    const campaign = groupDimension(group, 'campaign');
    const link = campaign ? byCampaign.get(campaign) : undefined;
    if (!link) return;
    const hit = perLink.get(link.id) ?? { link, metrics: [] };
    hit.metrics.push(groupMetrics(group));
    perLink.set(link.id, hit);
  });

  // Each asset's attributed links (versions).
  const perAsset = new Map<string, LinkFigures[]>();
  perLink.forEach(({ link, metrics }) => {
    const figures = figuresOf(link, metrics);
    if (figures.registrations <= 0 && figures.ftds <= 0 && figures.depositAmount <= 0) return;
    const key = `${link.kind}:${link.creative_id}`;
    const list = perAsset.get(key) ?? [];
    list.push(figures);
    perAsset.set(key, list);
  });

  // The asset row as spark-creatives joined it, from any of its links (a version's link may lack it).
  const assetOf = new Map<string, TrackingLink>();
  byCampaign.forEach((link) => {
    if (link.asset && !assetOf.has(link.creative_id)) assetOf.set(link.creative_id, link);
  });

  const rows: TopAssetRow[] = [];
  perAsset.forEach((links, key) => {
    const sorted = [...links].sort(byPerformance);
    const best = sorted[0].link;
    const withAsset = best.asset ? best : (assetOf.get(best.creative_id) ?? best);
    const registrations = links.reduce((sum, l) => sum + l.registrations, 0);
    const ftds = links.reduce((sum, l) => sum + l.ftds, 0);
    if (registrations <= 0 && ftds <= 0) return;
    const deposits = links.reduce((sum, l) => sum + l.deposits, 0);
    const depositAmount = links.reduce((sum, l) => sum + l.depositAmount, 0);
    // The other destinations, best first: other landing pages by name, the casino by its label.
    const bestDestination = destinationKey(best);
    const seen = new Set<string>([bestDestination]);
    const otherDestinations: string[] = [];
    sorted.forEach(({ link }) => {
      const dest = destinationKey(link);
      if (seen.has(dest)) return;
      seen.add(dest);
      otherDestinations.push(linkLandingPage(link)?.name || casinoLabel);
    });
    rows.push({
      key,
      link: best,
      name: linkAssetName(withAsset),
      imageUrl: withAsset.asset?.image_url ?? '',
      path: linkAssetPath(best),
      archived: withAsset.asset?.archived === true,
      landing: best.kind === 'landing_page' ? undefined : linkLandingPage(best),
      isLandingPage: best.kind === 'landing_page',
      otherDestinations,
      registrations,
      ftds,
      deposits,
      depositAmount,
    });
  });

  return rows.sort((a, b) => byPerformance(a, b) || a.name.localeCompare(b.name)).slice(0, limit);
};
